'use client'

import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form'
import { FullscreenMarkdownEditor } from '../FullscreenMarkdownEditor'
import type { DbAttachment } from '../MarkdownEditor/AttachmentsManager'

export type MarkdownAndAttachmentsValue = {
  markdown: { id?: string; content: string }
  attachments?: DbAttachment[]
}

type MarkdownAndAttachmentsFieldProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>
  control: Control<TFieldValues>
  height?: number
  markdownOnly?: boolean
  disabled?: boolean
  defaultMarkdownContent?: string
  preserveMarkdownId?: boolean
  onContentChange?: (markdown: string) => void
}

export function MarkdownAndAttachmentsField<TFieldValues extends FieldValues>({
  name,
  control,
  height,
  markdownOnly,
  disabled,
  defaultMarkdownContent = '',
  preserveMarkdownId = true,
  onContentChange
}: MarkdownAndAttachmentsFieldProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const currentValue = field.value as MarkdownAndAttachmentsValue | undefined
        const editorValue = currentValue
          ? {
              markdown: currentValue.markdown?.content ?? defaultMarkdownContent,
              attachments: currentValue.attachments ?? []
            }
          : undefined

        return (
          <FullscreenMarkdownEditor
            height={height}
            markdownOnly={markdownOnly}
            disabled={disabled}
            value={editorValue}
            onChange={next => {
              const nextMarkdown: MarkdownAndAttachmentsValue['markdown'] = {
                content: next.markdown
              }
              if (preserveMarkdownId && currentValue?.markdown?.id) {
                nextMarkdown.id = currentValue.markdown.id
              }
              field.onChange({
                markdown: nextMarkdown,
                attachments: next.attachments ?? []
              } satisfies MarkdownAndAttachmentsValue)
              onContentChange?.(next.markdown)
            }}
          />
        )
      }}
    />
  )
}
